import React from 'react';
import { FormData } from '../App';
import { Star, Shield } from 'lucide-react';

interface Props {
  formData: FormData;
  updateFormData: (data: Partial<FormData>) => void;
}

function HerbicideRating({ formData, updateFormData }: Props) {
  const handleRatingChange = (field: 'cropSafetyRating' | 'herbicideEfficacyRating', value: number) => {
    updateFormData({ [field]: value });
  };

  const RatingDropdown = ({ 
    value, 
    onChange, 
    title, 
    description,
    icon,
    color 
  }: { 
    value: number; 
    onChange: (value: number) => void; 
    title: string;
    description: string;
    icon: React.ReactNode;
    color: string;
  }) => (
    <div className="w-full p-6 border border-gray-200 rounded-lg bg-white shadow-sm">
      <div className="flex items-center gap-2 mb-4">
        {icon}
        <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
      </div>
      
      <div className="space-y-4">
        <select
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-${color} focus:border-transparent text-lg`}
        >
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
            <option key={rating} value={rating}>
              {rating} - {rating <= 3 ? 'Poor' : rating <= 6 ? 'Average' : rating <= 8 ? 'Good' : 'Excellent'}
            </option>
          ))}
        </select>
        
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-gray-200 rounded-full">
            <div
              className={`h-2 bg-${color} rounded-full transition-all duration-300`}
              style={{ width: `${(value / 10) * 100}%` }}
            ></div>
          </div>
          <span className="text-lg font-semibold min-w-[3rem] text-right">{value}/10</span>
        </div>

        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-800 mb-4">Herbicide Performance Rating</h2>
        <p className="text-gray-600 mb-6">
          Please rate the following aspects of your herbicide applications.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6">
        <RatingDropdown
          value={formData.cropSafetyRating}
          onChange={(value) => handleRatingChange('cropSafetyRating', value)}
          title="Crop Safety Rating"
          description="Rate how safe the herbicide applications were for your crop."
          icon={<Shield className="w-6 h-6 text-brand-blue" />}
          color="brand-blue"
        />

        <RatingDropdown
          value={formData.herbicideEfficacyRating}
          onChange={(value) => handleRatingChange('herbicideEfficacyRating', value)}
          title="Overall Herbicide Efficacy Rating"
          description="Rate the overall effectiveness of the herbicide applications in controlling weeds."
          icon={<Star className="w-6 h-6 text-brand-orange" />}
          color="brand-orange"
        />
      </div>
    </div>
  );
}

export default HerbicideRating;